




// Vue
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Mixins } from "vue-mixin-decorator";
import { mapGetters, mapActions } from "vuex";

// Third party
import dayjs from "dayjs";
import "dayjs/locale/es";

// Filters
import formatPhone from "@/shared/filters/formatPhone.filter.ts";
import formatDate from "@/shared/filters/formatDate.filter.ts";
import pluralizeFilter from "@/shared/filters/pluralize.filter.ts";
import round from "@/shared/filters/round.filter.ts";

// Constants
import Assets from "@/constants/assets.ts";
import Constants from "@/constants/constants.ts";
import Endpoints from "@/constants/endpoints";
import RouteNames from "@/constants/route-names";

// Components
import SelectTag from "@/shared/components/select.tag.component.vue";
import Icon, { IconEnum } from "@/shared/components/icon.component.vue";
import Btn, {
  BtnColor,
  BtnWidth,
  BtnPredefinedStyle,
} from "@/shared/components/btn.component.vue";
import Tooltip, {
  TooltipColor,
  TooltipAlign,
} from "@/shared/components/Tooltip.component.vue";
import FlatSelectorText, {
  FlatSelectorTextBorderBottom,
} from "@/shared/components/FlatSelectorText.component.vue";
import Modal, {
  ModalTitleColor,
} from "@/shared/components/Modal.component.vue";
import Spinner from "@/shared/components/Spinner.component.vue";
import Field from "@/shared/components/field.component.vue";
import SpotlightText, {
  SpotlightTextColor,
} from "@/shared/components/SpotlightText.component.vue";
import CheckIcon, {
  CheckIconColor,
} from "@/shared/components/CheckIcon.component.vue";
import TextArea from "@/shared/components/text-area.component.vue";
import FlatSelectorSwitch from "@/shared/components/flat-selector-switch.component.vue";
import FloatMenu, {
  FloatMenuPosition,
} from "@/shared/components/float-menu.component.vue";
import MenuElement, {
  MenuElementBgColor,
  MenuElementBgColorHover,
  MenuElementSize,
} from "@/shared/components/menu-element.component.vue";
import ButtonSwitch, {
  SwitchActiveColor,
  ButtonSwitchPredefinedStyle,
} from "@/shared/components/button-switch.component.vue";
import FieldSet from '@/shared/components/field-set.component.vue';
import SearchAutocomplete from '@/shared/components/SearchAutocomplete.component.vue';

// Views
import NoteItem from "@/manager/convocation/views/convocationDetail/noteItem/noteItem.view.vue";
import BudgetFees from "@/manager/budget/views/budget-row/budget-fees/index.vue";

// Models
import {
  BudgetPaymentStatus,
  BudgetPaymentStatusLiteral,
  BudgetCommunicationStatus,
  BudgetCommunicationStatusListeral,
  BudgetAlert,
  BudgetAlertLiteral,
} from "@/manager/budget/models/budget.model";

// Mixins
import WithSpinner from "@/shared/mixins/withSpinner.mixin.ts";

// Services
import { ConvocationService } from "@/manager/convocation/convocation.service";
import BudgetService from "@/manager/budget/budget.service";

enum IconBudgetColor {
  Red = "color-red",
  BlueDark = "color-blue-dark",
}

@Component({
  components: {
    SelectTag,
    Btn,
    BudgetFees,
    ButtonSwitch,
    CheckIcon,
    Field,
    FlatSelectorSwitch,
    FlatSelectorText,
    FloatMenu,
    Icon,
    MenuElement,
    Modal,
    NoteItem,
    SearchAutocomplete,
    Spinner,
    SpotlightText,
    TextArea,
    Tooltip,
    FieldSet
  },
  methods: {
    ...mapActions({
      setCurrentBudget: "setCurrentBudget",
      setCurrentBudgetAcceptance: 'setCurrentBudgetAcceptance',
    }),
  },
  computed: {
    ...mapGetters({
      getProfile: "getProfile",
      getBudget: "getBudget",
      getProcedencyList: "procedencyList",
      getBudgetNotes: "getBudgetNotes",
      allowEditBudget: "getAllowEditBudget",
      getAllowShowBudgetFees: "getAllowShowBudgetFees",
      getPurchaseNotes: "getPurchaseNotes",
      clientList: "getClientList"
    }),
  },
  filters: {
    formatDate,
    formatPhone,
    pluralizeFilter,
    round,
  },
})
export default class BudgetRow extends Mixins<WithSpinner>(WithSpinner) {
  @Prop() private rowNumber!: number;
  @Prop() private budgetId!: string;

  private assets = Assets;
  private openCollapsableInfo = false;
  private yetOpenedCollapsableInfo = false;
  private iconEnum = IconEnum;
  private tooltipColor = TooltipColor;
  private btnWidth = BtnWidth;
  private btnColor = BtnColor;
  private checkIconColor = CheckIconColor;
  private budgetAlertLiteral = BudgetAlertLiteral;
  private btnPredefinedClass = BtnPredefinedStyle;
  private modalTitleColor = ModalTitleColor;
  private menuElementSize = MenuElementSize;
  private menuElementBgColor = MenuElementBgColor;
  private menuElementBgColorHover = MenuElementBgColorHover;
  private floatMenuPosition = FloatMenuPosition;
  private budgetPaymentStatusLiteral = BudgetPaymentStatusLiteral;
  private budgetCommunicationStatus = BudgetCommunicationStatus;
  private getBudget!: any;
  private getProcedencyList!: any;
  private getBudgetNotes!: any;
  private getProfile!: any;
  private allowEditBudget!: boolean;
  private getAllowShowBudgetFees!: boolean;
  private setCurrentBudget!: any;
  private setCurrentBudgetAcceptance!: any;
  private spotlightTextColor = SpotlightTextColor;
  private paymentStatusSelected: any = {};
  private deleteBudgetModalOpened = false;
  private addObservationBudgetModalOpened = false;
  private sendAdminEmailModalOpened = false;
  private modalNoteEmpty = false;
  private modalNote = "";
  private constants = Constants;
  private timeSwitchSelected = this.constants.switchGenerals;
  private cambioDatosModalOpened = false;
  private clientPayments: any;
  private previousPaymentMethod: any;
  private getPurchaseNotes!: any;
  private colValue!: any;
  private searchClient: any = false;
  private mentions: any = [];
  private selectedClient!: any;
  private searchString = "";
  private searchTimeout: any = null

  @Watch("modalNote")
  private watchModalNote(newVal: any, oldVal: any) {
    if (newVal.slice(-1) == "$") {
      this.searchClient = true;
    } else {
      this.searchClient = false;
    }
  }

  @Watch("searchString")
  public watchsearchString(newVal: any, oldVal: any) {
    if (newVal && newVal.length > 3 && !this.selectedClient) {
      if (this.searchTimeout != null) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.searchCallback();
      }, 1000);
    }
  }

  public searchCallback() {
    if (this.searchString === "") {
      this.selectedClient = null;
    }
    this.fetchClientList();
  }

  private clearClient() {
    this.selectedClient = null;
  }

  private async selectClient(option: any) {
    this.selectedClient = option;
    this.searchClient = false;
    // console.log(this.selectedClient);
    this.modalNote += this.selectedClient.text;
    this.mentions.push(this.selectedClient)
  }

  private fetchClientList() {
    const payload = {
      limit: 20,
      search: this.searchString
    };
    this.$store.dispatch("fetchClientList", payload);
  }

  @Watch("paymentStatusSelected")
  private watchPaymentStatus(newVal: any, oldVal: any) {
    this.budget.paymentStatus = Number(newVal.value);
    this.patchBudget({ payment_status: this.budget.paymentStatus });
  }

  private created() {
    this.spinner.minimumTimeToShow = 500;
  }

  private mounted() {
    window.addEventListener('resize', this.handleResize);
  }

  private beforeDestroy() {
    // Remove the resize event listener when the component is destroyed
    window.removeEventListener('resize', this.handleResize);
  }

  private colspanValue() {
    // Dynamically determine colspan based on the display width
    this.colValue = this.$el.clientWidth <= 1500 ? 12 : 6;
  }

  private handleResize() {
    this.colspanValue()
    this.$forceUpdate(); // Force update to recalculate colspan on resize
  }


  get budget() {
    return this.getBudget(this.budgetId);
  }

  get showInfoIcon() {
    return this.budgetHasAlert || this.budgetHasDiscount;
  }

  get budgetHasDiscount() {
    return this.budget.discount > 0 || this.budget.discountReservation > 0;
  }

  get budgetHasAlert() {
    return this.budget.alert !== null;
  }

  get budgetClientTag() {
    return this.budget.client.tag.map((e: any) => {
      return { ...e, active: true };
    });
  }
  get budgetTags() {
    try {
      return (this.budget.purchase.tags || []).map((e: any) => {
        return { ...e, active: true };
      });
    } catch (_) {
      return [];
    }
  }

  get budgetStateTooltip() {
    const tooltip = {
      color: MenuElementBgColor.Secondary,
      text: BudgetCommunicationStatusListeral[this.budget.communicationStatus],
    };
    if (this.budgetIsAccepted) {
      tooltip.color = MenuElementBgColor.Primary;
    } else if (this.budgetIsRejected) {
      tooltip.color = MenuElementBgColor.Red;
    } else if (this.budgetIsDispatched) {
      tooltip.color = MenuElementBgColor.Secondary;
    }
    return tooltip;
  }

  get budgetIsDispatched() {
    return (
      this.budget.communicationStatus === BudgetCommunicationStatus.Dispatched
    );
  }

  get budgetIsAdjust() {
    return this.budget.communicationStatus === BudgetCommunicationStatus.Adjust;
  }

  get budgetIsAccepted() {
    return (
      this.budget.communicationStatus === BudgetCommunicationStatus.Accepted
    );
  }

  get budgetHasMandate() {
    return this.budget.mandateDocument;
  }

  get budgetIsRejected() {
    return (
      this.budget.communicationStatus === BudgetCommunicationStatus.Rejected
    );
  }

  get oddRow() {
    return this.rowNumber % 2 !== 0;
  }

  get observationsTooltip() {
    const numberOfNotes = this.budget.numberOfNotes;
    const pluralize = (this.$options as any).filters.pluralizeFilter(
      numberOfNotes,
      "observación",
      "observaciones"
    );
    return `${numberOfNotes} ${pluralize}`;
  }

  get budgetProducts() {
    let text = "Sin producto asociado";
    if (this.budget.budgetLine.length > 0) {
      text = this.budget.budgetLine[0].productName;
      const lines = this.budget.budgetLine.length;
      if (lines > 1) {
        text = `${lines} productos`;
      }
    }
    return text;
  }

  get noteListTransitionName() {
    let transitionName = "note-list";
    if (!this.yetOpenedCollapsableInfo) {
      transitionName += "-disabled";
    }
    return transitionName;
  }

  get hasConvocationOrigin() {
    return this.budget.convocationOrigin !== null;
  }

  get convocationDate() {
    return formatDate(this.budget.convocationOrigin.timetables[0].date, "DD MMM YY")
  }

  get routerLinkToPurchase() {
    const route = {
      name: RouteNames.PurchaseConvocationDetail,
      params: {
        id: this.budget.convocationOrigin.id,
      },
      query: {
        "search-string": this.budget.purchase.publicId,
      },
    };
    return route;
  }

  get budgetBackUrl() {
    const url = window.location.origin + "/api/admin/purchase/budget/" + this.budget.id + "/change"
    return url
  }

  get paymentStatusOptions() {
    const options = [];
    for (const paymentStatus in BudgetPaymentStatus) {
      if (Number(paymentStatus) >= 0) {
        options.push({
          value: paymentStatus,
          label: BudgetPaymentStatusLiteral[paymentStatus],
          colorSelected: this.paymentStatusColor(Number(paymentStatus)),
        });
      }
    }
    return options;
  }

  get paymentStatusOptionSelected() {
    return this.paymentStatusOptions.find((option) => {
      return option.value === this.budget.paymentStatus.toString();
    });
  }

  get budgetProcedency() {
    let result = "-";
    const convocationOrigin = this.budget.convocationOrigin;
    if (convocationOrigin) {
      const acronym = convocationOrigin.product.acronym || "-";
      const location = convocationOrigin.location.name || "-";
      const timetables = convocationOrigin.timetables;
      let date =
        timetables.length > 0
          ? convocationOrigin.timetables[0].date || "-"
          : "-";
      if (date !== "-") {
        date = dayjs(date, { locale: "es" }).format("DD MMM YY");
      }
      result = [acronym, location, date].join(" ");
    }
    return result;
  }

  get showNoteRow() {
    return this.openCollapsableInfo && !this.spinner.show;
  }

  get showSpinnerNoteRow() {
    return this.openCollapsableInfo && this.spinner.show;
  }

  get modalNotePlaceholder() {
    let placeholder =
      "Comparte alguna nota con tus compañeros sobre esta alta...";
    if (this.modalNoteEmpty) {
      placeholder = "Este campo no puede estar vacío...";
    }
    return placeholder;
  }

  get notes() {
    return this.getBudgetNotes(this.budgetId);
  }

  get purchaseNotes() {
    if (this.budget.purchase) {
      return this.getPurchaseNotes(this.budget.purchase.id);
    }
  }

  get budgetUrl() {
    let val = Endpoints.budgetDocument(this.budget.id);
    if (this.budget.document) {
      val = this.budget.document;
    }
    return val;
  }

  get mandateUrl() {
    let val = Endpoints.budgetDocument(this.budget.id);
    if (this.budget.mandateDocument) {
      val = this.budget.mandateDocument;
    }
    return val;
  }

  get flatSelectorCommunicationOptions() {
    const options = [
      {
        text: BudgetCommunicationStatusListeral[
          BudgetCommunicationStatus.Rejected
        ],
        color: TooltipColor.Red,
        iconColorClass: IconBudgetColor.Red,
        communicationStatus: BudgetCommunicationStatus.Rejected,
        condition: !this.budgetIsRejected,
      },
      {
        text: BudgetCommunicationStatusListeral[
          BudgetCommunicationStatus.Adjust
        ],
        color: TooltipColor.BlueDark,
        iconColorClass: IconBudgetColor.BlueDark,
        communicationStatus: BudgetCommunicationStatus.Adjust,
        condition: this.budgetIsAccepted,
      },
    ];
    return options;
  }

  get budgetTooltipActionText() {
    let text = "Enviar aceptación";
    if (this.budgetIsAdjust) {
      text = "Enviar validación";
    }
    return text;
  }

  get mandateHasBeenSigned() {
    return this.budget.sign;
  }

  get hasSoonPayment() {
    return this.budget.discountSoonPayment > 0;
  }

  get PendingPriceTooltip() {
    const tooltip = {
      title: "Restante",
      color: TooltipColor.Red,
    };
    if (this.hasSoonPayment) {
      tooltip.title = `Pronto pago: -${this.budget.discountSoonPayment}€`;
      tooltip.color = TooltipColor.Primary;
    }
    return tooltip;
  }

  get timeFilter(): any[] {
    return [
      this.constants.switchGenerals,
      this.constants.switchConvocations,
      this.constants.switchPurchases,
      this.constants.switchBudgets,
      this.constants.switchBills,
      this.constants.switchFiscalDataExtension,
      this.constants.switchPaymentMethods,
    ];
  }

  get clientProfile() {
    if (this.budget.client && this.budget.client.user && this.budget.client.user.id) {
      const route = {
        name: RouteNames.ClientEdit,
        params: {
          id: this.budget.client.user.id,
        },
      };
      return route;
    }
  }

  private toggleBudgetObservationModalOpened(value: boolean) {
    this.addObservationBudgetModalOpened = value;
    if (!value) {
      setTimeout(() => {
        this.mentions = [];
      }, 500);
      this.searchClient = false;
    }
  }

  private toggleSendAdminEmailModalOpened(value: boolean) {
    this.sendAdminEmailModalOpened = value;
  }

  private paymentStatusColor(paymentStatus: number) {
    let color = "";
    if (paymentStatus === BudgetPaymentStatus.Cancelled) {
      color = "red";
    } else if (
      paymentStatus === BudgetPaymentStatus.Completed ||
      paymentStatus === BudgetPaymentStatus.InProgress
    ) {
      color = "primary";
    } else if (
      paymentStatus === BudgetPaymentStatus.Unpaid ||
      paymentStatus === BudgetPaymentStatus.USAUnpaid
    ) {
      color = "orange";
    }
    return color;
  }

  private emitShowMessage(message: string, error: boolean = false) {
    this.$emit("show-message", { message, error });
  }

  private emitMessageError(response: any) {
    if (response.data && response.data.detail) {
      this.emitShowMessage(response.data.detail, true);
    }
  }

  private toggleCollapsableInfo() {
    this.openCollapsableInfo = !this.openCollapsableInfo;
    this.handleResize()
    if (this.openCollapsableInfo) {
      Vue.nextTick(() => {
        this.fetchPurchaseNotes()
        this.fetchBudgetNotes();
        this.fetchClientPayment(0)
      });
    }
  }

  private toogleDeleteBudgetModal(value: boolean) {
    this.deleteBudgetModalOpened = value;
  }

  private isLastBudgetLine(index: number) {
    const lines = this.budget.budgetLine.length - 1;
    return index === lines;
  }

  private deleteBudget() {
    this.$store.dispatch("deleteBudget", { id: this.budgetId }).then(() => {
      this.toogleDeleteBudgetModal(false);
      this.refetchList();
    });
  }

  private fetchPurchaseNotes() {
    this.requestShowSpinner();
    if (this.budget.purchase) {
      this.$store
        .dispatch("fetchPurchaseNotes", { id: this.budget.purchase.id })
        .then((res) => {
          this.changeSpinnerVisibility(false);
          if (!this.yetOpenedCollapsableInfo) {
            this.yetOpenedCollapsableInfo = true;
          }
        });
    }

  }

  private fetchBudgetNotes() {
    this.requestShowSpinner();
    this.$store
      .dispatch("fetchBudgetNotes", { id: this.budgetId })
      .then((res) => {
        this.changeSpinnerVisibility(false);
        if (!this.yetOpenedCollapsableInfo) {
          this.yetOpenedCollapsableInfo = true;
        }
      });
  }

  private addNote() {
    if (this.modalNote.length === 0) {
      this.modalNoteEmpty = true;
    } else {
      this.addObservationBudgetModalOpened = false;
      this.requestShowSpinner();
      this.$store
        .dispatch("newBudgetNote", {
          budgetId: this.budgetId,
          employee: { user: { id: this.getProfile.id } },
          note: this.modalNote,
          mentions: this.mentions,
        })
        .then((res) => {
          this.refreshNotes();
          this.mentions = [];
          this.searchString = ""
          this.searchClient = false
          this.clearClient()
          this.resetModalNoteEmpty(true);
          this.fetchBudgetNotes();
          this.changeSpinnerVisibility(false);
          this.refetchBudget();
        });
    }
  }

  private updateNote(noteId: string, noteContent: string, isPostponement?: string, mentions?: any[]) {
    const payload = {
      budgetId: this.budgetId,
      noteId,
      data: { note: noteContent, employeeModified: this.getProfile.id, isPostponement: isPostponement, mentions: mentions },
    };
    this.$store.dispatch("patchBudgetNote", payload).then((res: any) => {
      this.refreshNotes();
      this.mentions = [];
      this.searchString = ""
      this.searchClient = false
      this.clearClient()
    });
  }

  private refetchBudget() {
    this.$store.dispatch("fetchBudget", { id: this.budgetId });
  }

  private deleteNote(noteId: string) {
    const payload = {
      id: noteId,
      noteId,
    };
    this.$store.dispatch("removeBudgetNote", payload).then((res: any) => {
      this.refetchBudget();
    });
  }

  private refreshNotes() {
    this.requestShowSpinner();
    this.resetModalNoteEmpty(true);
    this.fetchBudgetNotes();
    this.changeSpinnerVisibility(false);
    this.refetchBudget();
  }

  private resetModalNoteEmpty(force: boolean) {
    if (force || this.modalNote === "") {
      this.modalNoteEmpty = false;
      this.modalNote = "";
    }
  }

  private editBudget() {
    const budgetToEdit = Object.assign({}, this.budget);
    budgetToEdit.budgetLine = Object.assign([], this.budget.budgetLine);
    this.setCurrentBudget(budgetToEdit);
    this.setCurrentBudgetAcceptance(budgetToEdit);
    this.$emit("edit-budget");
  }

  private refetchList() {
    this.$emit("refetch-list");
  }

  private patchBudget(data: any) {
    const payload = {
      id: this.budgetId,
      data,
    };
    this.$store.dispatch("patchBudget", payload);
  }

  private onSucess() {
    this.emitShowMessage("Email envíado correctamente");
  }

  private onError() {
    this.emitShowMessage(Constants.internalServerErrorMessage, true);
  }

  private sendAcceptanceEmail() {
    BudgetService.sendAcceptanceEmail(this.budgetId).then((res) => {
      this.budget.communicationStatus = BudgetCommunicationStatus.Dispatched;
      this.onSucess();
    }, this.onError);
  }

  private sendValidationEmail() {
    BudgetService.sendValidationEmail(this.budgetId).then(
      this.onSucess,
      this.onError
    );
  }

  private sendBudgetEmail() {
    if (this.budgetIsAdjust) {
      this.sendValidationEmail();
    } else {
      this.sendAcceptanceEmail();
    }
  }

  private sendDocumentEmail() {
    BudgetService.sendDocumentEmail(this.budgetId).then(
      this.onSucess,
      this.onError
    );
  }



  private timeFilterChanged(option: any) {
    const urlParams = new URLSearchParams();
    urlParams.append('search', option.requestName); // Añade el parámetro adicional
    const url = `${this.$router.resolve(this.clientProfile as any).href}?${urlParams.toString()}`;
    window.open(url, "_blank")
  }

  private seeDocument() {
    window.open(this.budgetUrl, "_blank");
  }

  private seeMandateDocument() {
    window.open(this.mandateUrl, "_blank");
  }

  get isOldTLP() {
    return this.budget.client.oldTopLevelPack;
  }

  get topLevelPackIconColor() {
    let color = this.checkIconColor.Gold;
    if (this.isOldTLP) {
      color = this.checkIconColor.GoldDark;
    }
    if (this.budget.client.tlp) {
      color = this.budget.client.tlp.color;
    }
    return color;
  }

  get topLevelPackTooltipColor() {
    // let color = this.tooltipColor.Gold;
    // if (this.isOldTLP) {
    //   color = this.tooltipColor.GoldDark;
    // }
    let color = this.tooltipColor.Secondary;
    // if (this.budget.client.tlp) {
    //   color = this.budget.client.tlp.color;
    // }
    return color;
  }

  get tooltipTextTLP() {
    let text = 'Top Level Pack';
    if (this.isOldTLP) {
      text = 'TLP Antiguo';
    }
    return text;
  }

  get powerPackIconColor() {
    let color = this.checkIconColor.Gold;
    if (this.budget.client.pwp) {
      color = this.budget.client.pwp.color;
    }
    return color;
  }

  get powerPackTooltipColor() {
    // let color = this.tooltipColor.Gold;
    // if (this.budget.client.pwp) {
    //   color = this.budget.client.pwp.color;
    // }
    let color = this.tooltipColor.Secondary;
    return color;
  }

  get tooltipTextPWP() {
    let text = 'Power Pack';
    return text;
  }

  get tooltipTextCN3() {
    let text = 'Certificado de nivel 3';
    return text;
  }

  private modifyDataPrivate() {
    this.cambioDatosModal(false)
    this.$emit('modify-data');
    this.editBudget();
  }

  private cambioDatosModal(value: boolean) {
    this.cambioDatosModalOpened = value;
  }

  private generateUrlChangeData() {
    var changeDataUrl = (window.location.href.split('#')[0] + '#/public/budget/modify/' + this.budget.id);
    this.cambioDatosModal(false);
    const navigatorAny:any = navigator
    navigatorAny.clipboard.writeText(changeDataUrl)
      .then(() => {
        this.emitShowMessage('Enlace copiado al portapapeles');
      })
      .catch((err:any) => {
        console.error('Error al copiar el enlace:', err);
      });
  }

  private setPreviousPaymentMethod() {
    var clientPaymentId = this.budget.creditCard ? this.budget.creditCard.id : this.budget.bankAccount ? this.budget.bankAccount.id : undefined;
    if (this.clientPayments && clientPaymentId) {
      this.previousPaymentMethod = this.clientPayments.filter((item: any) => item.id == clientPaymentId)[0]
      this.$forceUpdate();

      if (!this.previousPaymentMethod) {
        this.fetchClientPaymentDetail(clientPaymentId, 0)
      }
    }
  }

  private fetchClientPayment(viewStatus: number) {
    this.$store
      .dispatch("fetchClientPayment", { id: this.budget.client.user.id, viewStatus: viewStatus })
      .then((res) => {
        this.clientPayments = res.data
        this.setPreviousPaymentMethod()
      });
  }

  private fetchClientPaymentDetail(id: any, viewStatus: number) {
    this.$store
      .dispatch("fetchClientPaymentDetail", { id: id, viewStatus: viewStatus })
      .then((res) => {
        this.previousPaymentMethod = res.data[0]
        this.$forceUpdate();
      });
  }

}
